import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import ImageUploader from '../components/ImageUploader'
import { STATUS_FLAGS, EMPTY_STATUSES, getActiveStatuses } from '../utils/statusHelpers'

function findSimilarColors(input, existingVariants) {
  const norm = input.toLowerCase().trim()
  if (!norm) return []
  const inputWords = norm.split(/\s+/)
  return existingVariants.filter(v => {
    const existing = (v.variant_name || v.color_name || '').toLowerCase().trim()
    if (existing === norm) return false
    if (existing.includes(norm) || norm.includes(existing)) return true
    const existingWords = existing.split(/\s+/)
    const overlap = inputWords.filter(w => existingWords.includes(w)).length
    if (overlap > 0) {
      const score = overlap / Math.max(inputWords.length, existingWords.length)
      if (score >= 0.5) return true
    }
    return false
  })
}

function LureDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const [baseLure, setBaseLure] = useState(null)
  const [variants, setVariants] = useState([])
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [averageRating, setAverageRating] = useState(0)
  const [userStatuses, setUserStatuses] = useState({}) // { variantId: [array of status objects] }
  const [colorSearch, setColorSearch] = useState('')
  const [filteredVariants, setFilteredVariants] = useState([])
  const [showLimitedOnly, setShowLimitedOnly] = useState(false)
  const [variantSort, setVariantSort] = useState('az')

  // Modal state
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [newStatuses, setNewStatuses] = useState(EMPTY_STATUSES)
  const [newQuantity, setNewQuantity] = useState(1)
  const [newPriority, setNewPriority] = useState('Must Have')
  const [newCondition, setNewCondition] = useState('New')
  const [newNotes, setNewNotes] = useState('')
  const [newImages, setNewImages] = useState([])
  const [imageMetadata, setImageMetadata] = useState({ primaryIndex: 0, captions: {} })
  const [uploadingImages, setUploadingImages] = useState(false)

  // Forum preview state
  const [forumThreads, setForumThreads] = useState([])
  const [forumThreadCount, setForumThreadCount] = useState(0)

  // Add New Color Variant modal state
  const [showAddVariantModal, setShowAddVariantModal] = useState(false)
  const [newVColorName, setNewVColorName] = useState('')
  const [newVIsLE, setNewVIsLE] = useState(false)
  const [newVIntendedUse, setNewVIntendedUse] = useState('both')
  const [addVariantSubmitting, setAddVariantSubmitting] = useState(false)
  const [addVariantError, setAddVariantError] = useState('')
  const [addVariantSuccess, setAddVariantSuccess] = useState(false)
  const [similarColorWarnings, setSimilarColorWarnings] = useState([])
  const [confirmedUnique, setConfirmedUnique] = useState(false)

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [reviewRating, setReviewRating] = useState(7)
  const [reviewText, setReviewText] = useState('')
  const [externalLink, setExternalLink] = useState('')
  const [reviewImages, setReviewImages] = useState([])
  const [submittingReview, setSubmittingReview] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [reviewSuccess, setReviewSuccess] = useState('')

  const WISHLIST_PRIORITIES = [
    'Must Have',
    'Love to Have',
    'Like to Have',
    'Thinking About It',
    "Don't Buy This"
  ]

  useEffect(() => {
    fetchLureData()
    fetchForumPreview()
  }, [id])

  // NEW: Separate effect that runs when variants are loaded AND user is present
  useEffect(() => {
    if (user && variants.length > 0) {
      fetchUserStatuses()
    }
  }, [user, variants.length]) // Runs when user changes OR variants finish loading

  // Pre-populate review form when user's existing review loads
  useEffect(() => {
    if (!user || !reviews.length) return
    const existing = reviews.find(r => r.user_id === user.id)
    if (existing) {
      setReviewRating(existing.rating)
      setReviewText(existing.review_text || '')
      setExternalLink(existing.external_link || '')
    }
  }, [user?.id, reviews.length])

  useEffect(() => {
    if (!variants.length) return

    let filtered = [...variants]

    // Apply search filter
    if (colorSearch.trim()) {
      const searchLower = colorSearch.toLowerCase()
      filtered = filtered.filter(variant => {
        const colorName = (variant.variant_name || variant.color_name || '').toLowerCase()
        return colorName.includes(searchLower)
      })
    }

    // Apply Limited Edition filter
    if (showLimitedOnly) {
      filtered = filtered.filter(variant => variant.is_limited_edition)
    }

    // Apply sort
    if (variantSort === 'za') {
      filtered.sort((a, b) =>
        (a.variant_name || a.color_name || '').localeCompare(b.variant_name || b.color_name || '')
      )
      filtered.reverse()
    } else if (variantSort === 'le') {
      filtered.sort((a, b) => {
        if (b.is_limited_edition && !a.is_limited_edition) return 1
        if (a.is_limited_edition && !b.is_limited_edition) return -1
        return (a.variant_name || a.color_name || '').localeCompare(b.variant_name || b.color_name || '')
      })
    } else {
      // 'az' — default ascending
      filtered.sort((a, b) =>
        (a.variant_name || a.color_name || '').localeCompare(b.variant_name || b.color_name || '')
      )
    }

    setFilteredVariants(filtered)
  }, [colorSearch, showLimitedOnly, variantSort, variants])

  // Similarity check for Add New Color modal
  useEffect(() => {
    if (!newVColorName.trim() || !variants.length) {
      setSimilarColorWarnings([])
      return
    }
    setSimilarColorWarnings(findSimilarColors(newVColorName.trim(), variants))
    setConfirmedUnique(false)
  }, [newVColorName, variants])

  const isExactDuplicate = newVColorName.trim().length > 0 && variants.some(v =>
    (v.variant_name || v.color_name || '').toLowerCase().trim() === newVColorName.trim().toLowerCase()
  )

const fetchLureData = async () => {
  try {
    // Fetch base lure
    const { data: baseLureData, error: baseLureError } = await supabase
      .from('base_lures')
      .select(`
        *,
        brands (brand_name, website_url, country, description),
        lure_types (type_name, description),
        lure_subtypes (subtype_name)
      `)
      .eq('base_lure_id', id)
      .single()

    if (baseLureError) throw baseLureError

    // Fetch variants (no order clause - we'll sort in JavaScript)
    const { data: variantsData, error: variantsError } = await supabase
      .from('lure_variants')
      .select('*')
      .eq('base_lure_id', id)

    if (variantsError) throw variantsError

    // Sort alphabetically by color name
    const sortedVariants = (variantsData || []).sort((a, b) => {
      const colorA = a.variant_name || a.color_name || ''
      const colorB = b.variant_name || b.color_name || ''
      return colorA.localeCompare(colorB)
    })

    // Fetch reviews with images
    const { data: reviewsData, error: reviewsError } = await supabase
      .from('lure_reviews')
      .select(`
        *,
        users (username, full_name),
        review_images (image_id, image_url, upload_order)
      `)
      .eq('base_lure_id', id)
      .order('created_at', { ascending: false })

    if (reviewsError) throw reviewsError

    // Set state
    setBaseLure(baseLureData)
    setVariants(sortedVariants)
    setFilteredVariants(sortedVariants)
    setReviews(reviewsData || [])

    // Calculate average rating
    if (reviewsData && reviewsData.length > 0) {
      const avg = reviewsData.reduce((sum, r) => sum + r.rating, 0) / reviewsData.length
      setAverageRating(avg.toFixed(1))
    }

    setLoading(false)
  } catch (error) {
    console.error('Error fetching lure data:', error)
    setLoading(false)
  }
}

  const fetchForumPreview = async () => {
    try {
      const { data, count } = await supabase
        .from('forum_threads')
        .select('thread_id, title, created_at, updated_at, user_id, forum_posts(post_id)', { count: 'exact' })
        .eq('base_lure_id', id)
        .order('updated_at', { ascending: false })
        .limit(3)

      const userIds = [...new Set((data || []).map(t => t.user_id))]
      const { data: usersData } = userIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', userIds)
        : { data: [] }

      const userMap = {}
      usersData?.forEach(u => { userMap[u.id] = u.username })

      setForumThreads((data || []).map(t => ({
        ...t,
        username: userMap[t.user_id] || 'Unknown',
        postCount: t.forum_posts?.length || 0
      })))
      setForumThreadCount(count || 0)
    } catch (err) {
      console.error('Error fetching forum preview:', err)
    }
  }

  const fetchUserStatuses = async () => {
    if (!user) return
    
    // CRITICAL FIX: Only fetch if we have variants
    if (variants.length === 0) {
      console.log('No variants loaded yet, skipping status fetch')
      return
    }

    console.log('Fetching user statuses for', variants.length, 'variants')
    
    try {
      const variantIds = variants.map(v => v.variant_id)
      
      const { data, error } = await supabase
        .from('user_lure_inventory')
        .select('*')
        .eq('user_id', user.id)
        .in('variant_id', variantIds)

      if (error) throw error

      console.log('Found', data?.length || 0, 'status entries')

      // Group by variant_id
      const statusMap = {}
      data?.forEach(item => {
        if (!statusMap[item.variant_id]) {
          statusMap[item.variant_id] = []
        }
        statusMap[item.variant_id].push(item)
      })

      setUserStatuses(statusMap)
    } catch (error) {
      console.error('Error fetching user statuses:', error)
    }
  }

  const openStatusModal = (variant) => {
    setSelectedVariant(variant)
    setShowStatusModal(true)
    setNewStatuses(EMPTY_STATUSES)
    setNewQuantity(1)
    setNewPriority('Must Have')
    setNewCondition('New')
    setNewNotes('')
  }

const handleAddStatus = async () => {
    if (!user || !selectedVariant) return

    setUploadingImages(true)

    try {
      // Step 1: Insert the collection item first
      const insertData = {
        user_id: user.id,
        variant_id: selectedVariant.variant_id,
        ...newStatuses,
        quantity: newQuantity,
        condition: newCondition,
        notes: newNotes || null,
        wishlist_priority: newStatuses.status_wishlist ? newPriority : null
      }

      const { data: inventoryData, error: inventoryError } = await supabase
        .from('user_lure_inventory')
        .insert([insertData])
        .select()

      if (inventoryError) throw inventoryError

      const inventoryId = inventoryData[0].inventory_id
      console.log('Collection item created with ID:', inventoryId)

      // Step 2: Upload images if any were selected
      if (newImages.length > 0) {
        console.log(`Uploading ${newImages.length} images...`)
        
        for (let i = 0; i < newImages.length; i++) {
          const file = newImages[i]
          const isPrimary = i === imageMetadata.primaryIndex
          const caption = imageMetadata.captions[i] || ''

          try {
            // Upload to storage
            const { imageUrl, storagePath } = await uploadImageToStorage(file, inventoryId)
            
            // Save metadata to database
            await saveImageMetadata(inventoryId, imageUrl, storagePath, isPrimary, caption)
            
            console.log(`Image ${i + 1} uploaded successfully`)
          } catch (imgError) {
            console.error(`Failed to upload image ${i + 1}:`, imgError)
            // Continue with other images even if one fails
          }
        }
      }

      // Step 3: Close modal and refresh
      setShowStatusModal(false)
      setUploadingImages(false)
      fetchUserStatuses() // Refresh statuses after adding
      
      // Reset form
      setNewImages([])
      setImageMetadata({ primaryIndex: 0, captions: {} })
      
      const activeLabels = STATUS_FLAGS.filter(s => newStatuses[s.key]).map(s => s.label)
      alert(`Added to ${activeLabels.join(', ') || 'collection'}!${newImages.length > 0 ? ` With ${newImages.length} image(s)` : ''}`)
      
    } catch (error) {
      console.error('Error adding status:', error)
      setUploadingImages(false)
      
      if (error.code === '23505') {
        alert('You already have this status for this lure!')
      } else {
        alert('Failed to add status. Please try again.')
      }
    }
  }

  const handleRemoveStatus = async (inventoryId) => {
    if (!window.confirm('Remove this status?')) return

    try {
      const { error } = await supabase
        .from('user_lure_inventory')
        .delete()
        .eq('inventory_id', inventoryId)
        .eq('user_id', user.id)

      if (error) throw error

      fetchUserStatuses() // Refresh statuses after removing
    } catch (error) {
      console.error('Error removing status:', error)
      alert('Failed to remove status')
    }
  }

  // ============================================
  // ADD NEW COLOR VARIANT (direct, no approval)
  // ============================================

  const openAddVariantModal = () => {
    setNewVColorName('')
    setNewVIsLE(false)
    setNewVIntendedUse('both')
    setAddVariantError('')
    setAddVariantSuccess(false)
    setSimilarColorWarnings([])
    setConfirmedUnique(false)
    setShowAddVariantModal(true)
  }

  const handleAddNewVariant = async () => {
    const colorInput = newVColorName.trim()
    if (!colorInput) { setAddVariantError('Color name is required'); return }
    if (isExactDuplicate) { setAddVariantError('This color already exists for this lure.'); return }
    if (similarColorWarnings.length > 0 && !confirmedUnique) {
      setAddVariantError('Please confirm this is a different color from the similar ones listed below.')
      return
    }

    setAddVariantSubmitting(true)
    setAddVariantError('')

    try {
      const { error: insertError } = await supabase
        .from('lure_variants')
        .insert({
          base_lure_id: parseInt(id),
          color_name: colorInput,
          is_limited_edition: newVIsLE,
          intended_use: newVIntendedUse
        })

      if (insertError) throw insertError

      setAddVariantSuccess(true)
      await fetchLureData()
      setTimeout(() => {
        setShowAddVariantModal(false)
        setAddVariantSuccess(false)
      }, 1500)
    } catch (err) {
      console.error('Error adding variant:', err)
      if (err.code === '23505') {
        setAddVariantError('This color already exists for this lure.')
      } else {
        setAddVariantError(err.message || 'Failed to add color variant')
      }
    } finally {
      setAddVariantSubmitting(false)
    }
  }

  // ============================================
  // IMAGE UPLOAD HELPERS
  // ============================================

  /**
   * Upload a single image to Supabase Storage
   * Returns the public URL and storage path
   */
  const uploadImageToStorage = async (file, inventoryId) => {
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop()
      const timestamp = Date.now()
      const fileName = `${user.id}/${inventoryId}/${timestamp}.${fileExt}`

      // Upload to collection-images bucket
      const { error: uploadError } = await supabase.storage
        .from('collection-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: publicURL } = supabase.storage
        .from('collection-images')
        .getPublicUrl(fileName)

      return {
        imageUrl: publicURL.publicUrl,
        storagePath: fileName
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      throw error
    }
  }

  /**
   * Save image metadata to collection_images table
   */
  const saveImageMetadata = async (inventoryId, imageUrl, storagePath, isPrimary, caption) => {
    try {
      const { error } = await supabase
        .from('collection_images')
        .insert([{
          inventory_id: inventoryId,
          user_id: user.id,
          image_url: imageUrl,
          storage_path: storagePath,
          is_primary: isPrimary,
          caption: caption || null
        }])

      if (error) throw error
    } catch (error) {
      console.error('Error saving image metadata:', error)
      throw error
    }
  }

  /**
   * Handle image selection from ImageUploader component
   */
  const handleImagesChange = (files, metadata) => {
    setNewImages(files)
    setImageMetadata(metadata)
  }

  const uploadReviewImageToStorage = async (file, reviewId) => {
    const fileExt = file.name.split('.').pop()
    const fileName = `${user.id}/${reviewId}/${Date.now()}.${fileExt}`

    const { error: uploadError } = await supabase.storage
      .from('review-images')
      .upload(fileName, file, { cacheControl: '3600', upsert: false })

    if (uploadError) throw uploadError

    const { data: publicURL } = supabase.storage
      .from('review-images')
      .getPublicUrl(fileName)

    return { imageUrl: publicURL.publicUrl, storagePath: fileName }
  }

  const handleSubmitReview = async () => {
    if (!user) return
    if (!reviewText.trim() && reviewImages.length === 0) {
      setReviewError('Please write a review or add at least one image.')
      return
    }

    setSubmittingReview(true)
    setReviewError('')
    setReviewSuccess('')

    try {
      const existingReview = reviews.find(r => r.user_id === user.id)
      let reviewId

      if (existingReview) {
        const { error } = await supabase
          .from('lure_reviews')
          .update({
            rating: reviewRating,
            review_text: reviewText.trim() || null,
            external_link: externalLink.trim() || null
          })
          .eq('review_id', existingReview.review_id)

        if (error) throw error
        reviewId = existingReview.review_id
      } else {
        const { data, error } = await supabase
          .from('lure_reviews')
          .insert([{
            base_lure_id: parseInt(id),
            user_id: user.id,
            rating: reviewRating,
            review_text: reviewText.trim() || null,
            external_link: externalLink.trim() || null
          }])
          .select()

        if (error) throw error
        reviewId = data[0].review_id
      }

      for (let i = 0; i < reviewImages.length; i++) {
        try {
          const { imageUrl, storagePath } = await uploadReviewImageToStorage(reviewImages[i], reviewId)
          await supabase.from('review_images').insert([{
            review_id: reviewId,
            image_url: imageUrl,
            storage_path: storagePath,
            upload_order: i
          }])
        } catch (imgError) {
          console.error(`Failed to upload image ${i + 1}:`, imgError)
        }
      }

      setReviewSuccess(existingReview ? 'Review updated!' : 'Review submitted!')
      setShowReviewForm(false)
      setReviewImages([])
      await fetchLureData()

    } catch (error) {
      console.error('Error submitting review:', error)
      if (error.code === '23505') {
        setReviewError('You have already reviewed this lure.')
      } else {
        setReviewError(error.message || 'Failed to submit review.')
      }
    } finally {
      setSubmittingReview(false)
    }
  }


  const userReview = user ? reviews.find(r => r.user_id === user.id) : null
  const userHasLureInCollection = Object.keys(userStatuses).length > 0

  if (loading) {
    return <div className="loading">Loading lure details...</div>
  }

  if (!baseLure) {
    return (
      <div className="error-page">
        <h2>Lure Not Found</h2>
        <p>Sorry, we couldn't find that lure in our database.</p>
        <Link to="/browse" className="btn-primary">← Back to Browse</Link>
      </div>
    )
  }

  return (
    <div className="lure-detail-page">
      <div className="breadcrumb">
        <Link to="/">Home</Link> → 
        <Link to="/browse">Browse Lures</Link> → 
        <span>{baseLure.name}</span>
      </div>

      {baseLure.image_url && (
        <div className="lure-detail-image-container">
          <img src={baseLure.image_url} alt={baseLure.name} className="lure-detail-image" />
        </div>
      )}

      <div className="lure-header">
        <div className="lure-header-left">
          <h1>{baseLure.name}</h1>
          <p className="lure-subtitle">
            {baseLure.brands?.brand_name} | {baseLure.lure_types?.type_name}
            {baseLure.lure_subtypes && ` - ${baseLure.lure_subtypes.subtype_name}`}
          </p>
        </div>
        <div className="lure-header-right">
          {reviews.length > 0 && (
            <div className="rating-box">
              <div className="rating-number">{averageRating}</div>
              <div className="rating-details">
                <div className="rating-stars">{'⭐'.repeat(Math.round(averageRating))}</div>
                <div className="rating-count">{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}</div>
              </div>
            </div>
          )}
          {reviews.length === 0 && (
            <div className="rating-box no-rating">
              <p>No ratings yet</p>
              <p className="small">Be the first to review!</p>
            </div>
          )}
        </div>
      </div>

      <div className="lure-content-grid">
        <div className="lure-main-content">
          {/* Specifications */}
          <section className="info-section">
            <h2>Specifications</h2>
            <div className="specs-grid">
              <div className="spec-item">
                <span className="spec-label">Brand:</span>
                <span className="spec-value">{baseLure.brands?.brand_name}</span>
              </div>
              <div className="spec-item">
                <span className="spec-label">Type:</span>
                <span className="spec-value">{baseLure.lure_types?.type_name}</span>
              </div>
              {baseLure.lure_subtypes && (
                <div className="spec-item">
                  <span className="spec-label">Subtype:</span>
                  <span className="spec-value">{baseLure.lure_subtypes.subtype_name}</span>
                </div>
              )}
              {baseLure.weight && (
                <div className="spec-item">
                  <span className="spec-label">Weight:</span>
                  <span className="spec-value">{baseLure.weight}</span>
                </div>
              )}
              {baseLure.length && (
                <div className="spec-item">
                  <span className="spec-label">Length:</span>
                  <span className="spec-value">{baseLure.length}</span>
                </div>
              )}
              {baseLure.depth_range && (
                <div className="spec-item">
                  <span className="spec-label">Depth Range:</span>
                  <span className="spec-value">{baseLure.depth_range}</span>
                </div>
              )}
            </div>
          </section>

          {/* Description */}
          {baseLure.description && (
            <section className="info-section">
              <h2>Description</h2>
              <p className="description-text">{baseLure.description}</p>
            </section>
          )}

          {/* Brand Info */}
          {baseLure.brands && (
            <section className="info-section">
              <h2>About {baseLure.brands.brand_name}</h2>
              <div className="brand-info">
                {baseLure.brands.description && (
                  <p className="description-text" style={{ marginBottom: '1.5rem' }}>
                    {baseLure.brands.description}
                  </p>
                )}
                {baseLure.brands.country && <p><strong>Country:</strong> {baseLure.brands.country}</p>}
                {baseLure.brands.website_url && (
                  <p>
                    <strong>Website:</strong>{' '}
                    <a href={baseLure.brands.website_url} target="_blank" rel="noopener noreferrer">
                      {baseLure.brands.website_url}
                    </a>
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Available Colors */}
          <section className="info-section">
            <div className="section-header-row">
              <h2>Available Colors ({variants.length})</h2>
              {user && (
                <button onClick={openAddVariantModal} className="btn-outline-subtle">
                  + Add New Color
                </button>
              )}
            </div>

            {/* Search and Filter Panel */}
            {variants.length > 0 && (
              <div className="color-search-panel">
                <div className="color-search-row">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="Search colors... (e.g., 'shad', 'ghost', 'chartreuse')"
                      value={colorSearch}
                      onChange={(e) => setColorSearch(e.target.value)}
                      className="color-search-input"
                    />
                    {colorSearch && (
                      <button
                        onClick={() => setColorSearch('')}
                        className="search-clear-btn"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <select
                    value={variantSort}
                    onChange={(e) => setVariantSort(e.target.value)}
                    className="variant-sort-select"
                  >
                    <option value="az">A → Z</option>
                    <option value="za">Z → A</option>
                    <option value="le">Limited Edition First</option>
                  </select>

                  <label className="limited-filter-label">
                    <input
                      type="checkbox"
                      checked={showLimitedOnly}
                      onChange={(e) => setShowLimitedOnly(e.target.checked)}
                    />
                    <span>Limited Edition Only</span>
                  </label>
                </div>

                <div className="search-results-info">
                  Showing {filteredVariants.length} of {variants.length} colors
                  {colorSearch && ` matching "${colorSearch}"`}
                  {showLimitedOnly && ' (Limited Edition)'}
                </div>
              </div>
            )}

            {/* Variants Grid */}
            <div className="variants-grid">
              {filteredVariants.map((variant) => {
                const userStatusesForVariant = userStatuses[variant.variant_id] || []
                
                return (
                  <div key={variant.variant_id} className="variant-card">
                    {variant.image_url && (
                      <div className="variant-image-container">
                        <img src={variant.image_url} alt={variant.color_name} className="variant-image" />
                      </div>
                    )}
                    
                    <div className="variant-header">
                      <div className="variant-color">
                        <span className="variant-name">
                          {variant.variant_name || variant.color_name}
                        </span>
                      </div>
                      {variant.is_limited_edition && (
                        <span className="limited-badge">Limited Edition</span>
                      )}
                      {variant.intended_use && variant.intended_use !== 'both' && (
                        <span className={`intended-use-badge ${variant.intended_use === 'collection' ? 'badge-collectible-only' : 'badge-fishing-only'}`}>
                          {variant.intended_use === 'collection' ? 'Collectible' : 'Fishing Only'}
                        </span>
                      )}
                    </div>

                    {userStatusesForVariant.length > 0 && (
                      <div className="variant-current-statuses">
                        {userStatusesForVariant.map(status => (
                          <div key={status.inventory_id} className="status-badge-item">
                            <div className="multi-status-badge-wrap">
                              {getActiveStatuses(status).map(s => (
                                <span key={s.key} className={`status-badge ${s.cssClass}`}>{s.label}</span>
                              ))}
                              <span className="status-qty">({status.quantity})</span>
                              {status.status_wishlist && status.wishlist_priority && (
                                <span className="priority-text"> - {status.wishlist_priority}</span>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemoveStatus(status.inventory_id)}
                              className="btn-remove-status"
                              title="Remove this status"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="variant-actions">
                      {user ? (
                        <button
                          onClick={() => openStatusModal(variant)}
                          className="btn-add-variant"
                        >
                          + Add Status
                        </button>
                      ) : (
                        <button className="btn-add-variant" disabled>
                          Login to Add
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* No Results Message */}
            {filteredVariants.length === 0 && variants.length > 0 && (
              <div className="no-variants">
                <p>No colors found matching your search.</p>
                <button 
                  onClick={() => { 
                    setColorSearch(''); 
                    setShowLimitedOnly(false); 
                  }} 
                  className="btn-secondary"
                >
                  Clear Filters
                </button>
              </div>
            )}

            {variants.length === 0 && (
              <p className="no-variants">No color variants available yet.</p>
            )}
          </section>

          {/* Reviews Section */}
          <section className="info-section reviews-section">
            <h2>User Reviews ({reviews.length})</h2>

            {reviewSuccess && (
              <div className="contextual-banner contextual-banner-success" style={{ marginBottom: '1rem' }}>
                <p>✅ {reviewSuccess}</p>
              </div>
            )}

            {/* Write / Edit review CTA */}
            {user ? (
              !userHasLureInCollection ? (
                <p className="review-collection-note">
                  Add this lure to your collection above to write a review.
                </p>
              ) : !showReviewForm ? (
                <button
                  className="btn-primary"
                  style={{ marginBottom: '1.5rem' }}
                  onClick={() => setShowReviewForm(true)}
                >
                  {userReview ? '✏️ Edit My Review' : '✍️ Write a Review'}
                </button>
              ) : (
                <div className="review-form">
                  <h3>{userReview ? 'Edit Your Review' : 'Write a Review'}</h3>

                  {reviewError && (
                    <div className="alert-error" style={{ marginBottom: '1rem' }}>{reviewError}</div>
                  )}

                  <div className="form-group">
                    <label><strong>Rating *</strong></label>
                    <div className="rating-picker">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setReviewRating(n)}
                          className={`rating-btn ${reviewRating === n ? 'active' : ''}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <p className="rating-selected-label">{reviewRating}/10</p>
                  </div>

                  <div className="form-group">
                    <label><strong>Your Review</strong></label>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      rows="5"
                      className="form-textarea"
                      placeholder="Share your experience — how does it cast, what fish did you catch, when does it shine?"
                    />
                  </div>

                  <div className="form-group">
                    <label><strong>Social Media Link (Optional)</strong></label>
                    <input
                      type="url"
                      value={externalLink}
                      onChange={(e) => setExternalLink(e.target.value)}
                      className="form-input"
                      placeholder="YouTube, Instagram, TikTok, etc."
                    />
                  </div>

                  <ImageUploader
                    maxImages={5}
                    onImagesChange={(files) => {
                      setReviewImages(files)
                    }}
                    existingImages={userReview?.review_images || []}
                  />

                  <div className="modal-footer" style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => { setShowReviewForm(false); setReviewError('') }}
                      className="btn-secondary"
                      disabled={submittingReview}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmitReview}
                      className="btn-primary"
                      disabled={submittingReview}
                    >
                      {submittingReview ? 'Submitting...' : (userReview ? 'Update Review' : 'Submit Review')}
                    </button>
                  </div>
                </div>
              )
            ) : (
              <p className="review-collection-note">
                <Link to="/login">Log in</Link> and add this lure to your collection to write a review.
              </p>
            )}

            {reviews.length === 0 && (
              <div className="no-reviews">
                <p>No reviews yet for this lure. Be the first to share your experience!</p>
              </div>
            )}

            {reviews.map((review) => (
              <div key={review.review_id} className="review-card">
                <div className="review-header">
                  <div className="review-author">
                    {review.users?.username ? (
                      <Link to={`/profile/${review.user_id}`} className="review-username-link">
                        <strong>{review.users.username}</strong>
                      </Link>
                    ) : (
                      <strong>Anonymous</strong>
                    )}
                    <span className="review-date">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                    {review.updated_at !== review.created_at && (
                      <span className="review-edited"> (edited)</span>
                    )}
                  </div>
                  <div className="review-rating">
                    <span className="rating-badge">{review.rating}/10</span>
                  </div>
                </div>

                {review.review_text && (
                  <div className="review-body">
                    <p>{review.review_text}</p>
                  </div>
                )}

                {review.review_images && review.review_images.length > 0 && (
                  <div className="review-images-grid">
                    {review.review_images
                      .sort((a, b) => a.upload_order - b.upload_order)
                      .map(img => (
                        <img
                          key={img.image_id}
                          src={img.image_url}
                          alt="Review photo"
                          className="review-image"
                        />
                      ))}
                  </div>
                )}

                {review.external_link && (
                  <div className="review-external-link">
                    <a href={review.external_link} target="_blank" rel="noopener noreferrer">
                      🔗 View on social media
                    </a>
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* ── Discussions Preview ── */}
          <section className="discussions-preview-section">
            <div className="section-header-row">
              <h2>Discussions</h2>
              <Link to={`/lure/${id}/discussions`} className="btn-secondary btn-sm">
                View All ({forumThreadCount})
              </Link>
            </div>

            {forumThreads.length === 0 ? (
              <p className="no-discussions">No discussions yet — be the first to start one!</p>
            ) : (
              <div className="forum-thread-list">
                {forumThreads.map(thread => (
                  <Link
                    key={thread.thread_id}
                    to={`/lure/${id}/discussions/${thread.thread_id}`}
                    className="forum-thread-preview"
                  >
                    <div className="thread-preview-title">{thread.title}</div>
                    <div className="thread-preview-meta">
                      by {thread.username}
                      {' · '}{thread.postCount} {thread.postCount === 1 ? 'post' : 'posts'}
                      {' · '}{new Date(thread.updated_at).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div style={{ marginTop: '1rem' }}>
              {user ? (
                <Link to={`/lure/${id}/discussions?new=1`} className="btn-primary">
                  + Start a Discussion
                </Link>
              ) : (
                <p className="forum-login-note">
                  <Link to="/login">Log in</Link> to start a discussion.
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Right Sidebar */}
        <div className="lure-sidebar">
          <div className="sidebar-section">
            <h3>Community</h3>
            <div className="community-stats">
              <div className="stat">
                <span className="stat-number">{reviews.length}</span>
                <span className="stat-label">Reviews</span>
              </div>
              <div className="stat">
                <span className="stat-number">{forumThreadCount}</span>
                <span className="stat-label">Discussions</span>
              </div>
              <div className="stat">
                <span className="stat-number">{variants.length}</span>
                <span className="stat-label">Colors</span>
              </div>
            </div>
          </div>

          <div className="sidebar-section">
            <h3>Quick Actions</h3>
            {user ? (
              <>
                <p className="sidebar-note">Select a color above to add to your collection</p>
                <button
                  className="sidebar-btn"
                  onClick={() => {
                    if (!userHasLureInCollection) {
                      alert('Add this lure to your collection first to write a review.')
                      return
                    }
                    setShowReviewForm(true)
                    document.querySelector('.reviews-section')?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {userReview ? '✏️ Edit My Review' : '✍️ Write a Review'}
                </button>
              </>
            ) : (
              <>
                <p className="sidebar-note">Login to add lures to your collection and write reviews</p>
                <Link to="/login" className="sidebar-btn-link">
                  Login / Sign Up
                </Link>
              </>
            )}
          </div>

          <div className="sidebar-section">
            <h3>Quick Stats</h3>
            <div className="quick-stats">
              {averageRating > 0 && (
                <p><strong>Avg Rating:</strong> {averageRating}/10</p>
              )}
              <p><strong>Type:</strong> {baseLure.lure_types?.type_name}</p>
              <p><strong>Brand:</strong> {baseLure.brands?.brand_name}</p>
              <p><strong>Colors Available:</strong> {variants.length}</p>
            </div>
          </div>
        </div>
      </div>

{/* Add Status Modal */}
      {showStatusModal && selectedVariant && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add to Collection</h2>
              <button onClick={() => setShowStatusModal(false)} className="modal-close">✕</button>
            </div>

            <div className="modal-body">
              <p className="modal-variant-name">
                {baseLure.name} - {selectedVariant.color_name}
              </p>

              {/* ADD ERROR DISPLAY */}
              {uploadingImages && (
                <div className="alert alert-info">
                  <p>📤 Uploading images, please wait...</p>
                </div>
              )}

              <div className="form-group">
                <label>Collection Status (select all that apply)</label>
                <div className="status-checkbox-group">
                  {STATUS_FLAGS.map(s => (
                    <label key={s.key} className="status-checkbox-label">
                      <input
                        type="checkbox"
                        checked={newStatuses[s.key] || false}
                        onChange={e => setNewStatuses(prev => ({ ...prev, [s.key]: e.target.checked }))}
                      />
                      <span className={`collection-status-pill ${s.cssClass}`}>{s.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {newStatuses.status_wishlist && (
                <div className="form-group">
                  <label>Wishlist Priority *</label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="form-select"
                  >
                    {WISHLIST_PRIORITIES.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  min="1"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(parseInt(e.target.value))}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Condition</label>
                <select
                  value={newCondition}
                  onChange={(e) => setNewCondition(e.target.value)}
                  className="form-select"
                >
                  <option value="New">New</option>
                  <option value="Like New">Like New</option>
                  <option value="Very Good">Very Good</option>
                  <option value="Good">Good</option>
                  <option value="Acceptable">Acceptable</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="form-textarea"
                  rows="3"
                  placeholder="Add any notes about this item..."
                ></textarea>
              </div>

              {/* ============================================
                  ADD IMAGE UPLOADER COMPONENT HERE
                  ============================================ */}
              
              <ImageUploader
                maxImages={5}
                onImagesChange={handleImagesChange}
                existingImages={[]}
              />

            </div>

            <div className="modal-footer">
              <button 
                onClick={() => setShowStatusModal(false)} 
                className="btn-secondary"
                disabled={uploadingImages}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddStatus} 
                className="btn-primary"
                disabled={uploadingImages}
              >
                {uploadingImages ? 'Uploading...' : 'Add to Collection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Color Variant Modal */}
      {showAddVariantModal && (
        <div className="modal-overlay" onClick={() => setShowAddVariantModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Color Variant</h2>
              <button onClick={() => setShowAddVariantModal(false)} className="modal-close">✕</button>
            </div>

            <div className="modal-body">
              <p className="modal-variant-name">{baseLure?.name}</p>

              {addVariantSuccess ? (
                <div className="success-message" style={{ margin: '1rem 0' }}>
                  ✅ Color variant added!
                </div>
              ) : (
                <>
                  {addVariantError && (
                    <div className="alert-error" style={{ marginBottom: '1rem' }}>{addVariantError}</div>
                  )}

                  <div className="form-group">
                    <label><strong>Color Name *</strong></label>
                    <input
                      type="text"
                      value={newVColorName}
                      onChange={(e) => setNewVColorName(e.target.value)}
                      placeholder="e.g., Sexy Shad, Ghost Minnow"
                      className="form-input"
                      autoFocus
                    />
                    {isExactDuplicate && (
                      <p className="field-hint field-error-text">This color already exists for this lure.</p>
                    )}
                  </div>

                  {similarColorWarnings.length > 0 && !isExactDuplicate && (
                    <div className="similarity-warning">
                      <p><strong>Similar colors already exist for this lure:</strong></p>
                      <ul className="similarity-list">
                        {similarColorWarnings.map(v => (
                          <li key={v.variant_id}>{v.variant_name || v.color_name}</li>
                        ))}
                      </ul>
                      <label className="status-checkbox-label" style={{ marginTop: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={confirmedUnique}
                          onChange={(e) => setConfirmedUnique(e.target.checked)}
                        />
                        <span>I've confirmed this is a different color</span>
                      </label>
                    </div>
                  )}

                  <div className="form-group">
                    <label className="status-checkbox-label">
                      <input
                        type="checkbox"
                        checked={newVIsLE}
                        onChange={(e) => setNewVIsLE(e.target.checked)}
                      />
                      <span>Limited Edition</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label><strong>Intended Use</strong></label>
                    <select
                      value={newVIntendedUse}
                      onChange={(e) => setNewVIntendedUse(e.target.value)}
                      className="form-select"
                    >
                      <option value="both">Fishing & Collectible</option>
                      <option value="collection">Collectible Only</option>
                      <option value="fishing">Fishing Only</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            {!addVariantSuccess && (
              <div className="modal-footer">
                <button onClick={() => setShowAddVariantModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleAddNewVariant}
                  className="btn-primary"
                  disabled={
                    addVariantSubmitting ||
                    !newVColorName.trim() ||
                    isExactDuplicate ||
                    (similarColorWarnings.length > 0 && !confirmedUnique)
                  }
                >
                  {addVariantSubmitting ? 'Adding...' : 'Add Color Variant'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  )
}

export default LureDetail